import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { Grid2 as Grid, Box, IconButton, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FormikTextField } from './FormikTextField';
import { FormikSubmitButton } from './FormikSubmitButton';
import type { AMC } from '../services/amcApi';
import { FormikSwitch } from './FormikSwitch';
import { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Delete } from '@mui/icons-material';
import AddCircleIcon from '@mui/icons-material/AddCircle';

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
  fundCode: yup.string().required('Fund Code is required'),
});

export interface AMCFormValues extends Omit<AMC, 'id'> {
  id?: string;
}

interface AMCFormProps {
  initialValues: AMCFormValues;
  onSubmit: (values: AMCFormValues) => Promise<void>;
  submitButtonText: string;
}

export const AMCForm = ({ initialValues, onSubmit, submitButtonText }: AMCFormProps) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [logo, setLogo] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | string | null>(initialValues?.logoPath || null);
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await onSubmit({ ...values, ...(logo && { logo }) });
          setLogo(null);
          setLogoFile(null);
          enqueueSnackbar('AMC saved successfully', { variant: 'success' });
        } catch (error: any) {
          enqueueSnackbar(error.data?.message || 'Failed to save AMC', { variant: 'error' });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      <Form>
        <Grid container sx={{ flexGrow: 1 }} spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormikTextField name="name" label="Name" fullWidth />
              <FormikTextField name="fundCode" label="ID" fullWidth />
              <FormikSwitch name="isRTA" label="Is RTA" />
              <FormikSwitch name="useExternalUpiWebhook" label="Force Poll Query API" />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                width: '100%',
              }}
            >
              {logoFile ? (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <img
                    src={logoFile instanceof File ? URL.createObjectURL(logoFile) : logoFile}
                    alt="AMC Logo"
                    style={{ height: '200px', margin: '0 auto' }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      right: '0%',
                      transform: 'translate(-50%, -50%)',
                      color: theme.palette.common.white,
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    <IconButton
                      onClick={() => {
                        setLogo(null);
                        setLogoFile(null);
                      }}
                    >
                      <Delete color="error" />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <label htmlFor="logo" style={{ width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                    }}
                  >
                    <AddCircleIcon color="primary" />
                    <Typography variant="body2" textAlign="center">
                      Click to Upload Logo
                      <br />
                      Max size: 2MB
                      <br />
                      Supports: jpeg, png, jpg
                    </Typography>
                    <input
                      id="logo"
                      name="logo"
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          setLogoFile(e.target.files[0]);
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            if (event.target) {
                              setLogo(event.target.result as string);
                            }
                          };
                          reader.readAsDataURL(e.target.files[0]);
                        }
                      }}
                    />
                  </Box>
                </label>
              )}
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <FormikSubmitButton>{submitButtonText}</FormikSubmitButton>
        </Box>
      </Form>
    </Formik>
  );
};
